import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { isOfflineSessionKind } from '@/constants/session-kind'
import { useStore } from '@/store/useStore'
import MeetingChat from './components/MeetingChat'
import { resolveRouteSessionKind } from './utils/resolveRouteSessionKind'

/**
 * Live-recording page mounted at `/meetings/:id/live`. The `:id` is the
 * backend UUID assigned by `POST /api/meetings` (already seeded into the
 * store by the create-meeting modal). We simply confirm the store's
 * `currentMeetingId` matches the route; otherwise redirect back home so
 * we never render the live UI against a phantom meeting.
 */
export default function MeetingsPage() {
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const routeMeetingId = params.id ?? null

  const theme = useStore((s) => s.theme)
  const meetings = useStore((s) => s.meetings)
  const currentMeetingId = useStore((s) => s.currentMeetingId)
  const selectMeeting = useStore((s) => s.selectMeeting)
  const settingsSessionKind = useStore((s) => s.settings.meetingSetup.sessionKind)
  const pendingStartMeetingId = useStore((s) => s.settings.pendingStartMeetingId)

  // Tracks whether the current route's meeting has ever been confirmed to
  // exist in the store, so a later removal (e.g. `deleteMeeting` called by
  // the end-meeting flow right before navigating to the saved detail page)
  // isn't mistaken for the "never existed" case below.
  const meetingConfirmedRef = useRef(false)

  useEffect(() => {
    meetingConfirmedRef.current = false
  }, [routeMeetingId])

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
  }, [theme])

  useEffect(() => {
    if (!routeMeetingId) {
      navigate('/home', { replace: true })
      return
    }

    const routeSessionKind = resolveRouteSessionKind(routeMeetingId, {
      meetings,
      pendingStartMeetingId,
      settingsSessionKind,
    })
    if (isOfflineSessionKind(routeSessionKind)) {
      navigate(`/meetings/${routeMeetingId}/record`, { replace: true })
      return
    }

    const meetingExists = meetings.some((m) => m.id === routeMeetingId)
    if (meetingExists) {
      meetingConfirmedRef.current = true
    } else if (!meetingConfirmedRef.current) {
      // The store was purged (hard reload before the create-meeting flow
      // seeded it, or a stale link). Send the user home rather than render
      // an empty MeetingChat with a "select meeting" placeholder.
      navigate('/home', { replace: true })
      return
    }

    if (currentMeetingId !== routeMeetingId) {
      selectMeeting(routeMeetingId)
    }
  }, [
    currentMeetingId,
    meetings,
    navigate,
    pendingStartMeetingId,
    routeMeetingId,
    selectMeeting,
    settingsSessionKind,
  ])

  return (
    <>
      <div className="flex h-[100dvh] flex-col overflow-hidden bg-white text-slate-950 dark:bg-[var(--bg-secondary)] dark:text-[var(--text-primary)]">
        <MeetingChat />
      </div>
    </>
  )
}
